import React, { useEffect, useState, useCallback } from "react";
import { api } from "../api";
import { Users, Eye, MessageCircle, FileDown, Mail, TrendingUp, Globe } from "lucide-react";

const SOURCE_LABEL = {
  google: "Google",
  search: "Arama Motoru",
  social: "Sosyal Medya",
  referral: "Diğer Siteler",
  direct: "Doğrudan",
};

const PATH_LABEL = {
  "/": "Ana Sayfa",
  "/shop": "Koleksiyonlar",
  "/all-projects": "Projeler",
  "/akademi": "Akademi",
  "/about": "Hakkımızda",
  "/heritage": "Miras",
  "/how-we-do": "Nasıl Çalışıyoruz",
  "/contact": "İletişim",
};

const pathLabel = (p) => {
  if (PATH_LABEL[p]) return PATH_LABEL[p];
  if (p.startsWith("/product-category/")) return `Kategori: ${p.split("/").pop()}`;
  if (p.startsWith("/urun/")) return `Ürün: ${p.split("/").pop()}`;
  return p;
};

const Metric = ({ icon: Icon, label, value, accent, testid }) => (
  <div data-testid={`metric-${testid || label}`} className="bg-white border border-[#e6ddd0] p-5 flex items-center gap-4">
    <span className="w-12 h-12 flex items-center justify-center rounded-full" style={{ background: `${accent}1a`, color: accent }}>
      <Icon size={22} />
    </span>
    <div>
      <p className="text-2xl font-serif text-[#211d18] leading-none">{value}</p>
      <p className="text-[12px] uppercase tracking-wider text-[#8b8271] mt-1">{label}</p>
    </div>
  </div>
);

const AdminDashboard = () => {
  const [days, setDays] = useState(30);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const d = await api.analytics(days);
      setData(d);
    } catch (e) {
      setData(null);
    } finally {
      setLoading(false);
    }
  }, [days]);

  useEffect(() => { load(); }, [load]);

  const maxDaily = data ? Math.max(1, ...data.daily.map((x) => x.count)) : 1;
  const totalSessions = data ? data.sources.reduce((s, x) => s + x.count, 0) || 1 : 1;

  return (
    <div data-testid="admin-dashboard">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-serif text-2xl text-[#211d18]">Site Raporu</h2>
        <div className="inline-flex border border-[#d8ccb8] rounded overflow-hidden">
          {[7, 30].map((n) => (
            <button
              key={n}
              data-testid={`range-${n}`}
              onClick={() => setDays(n)}
              className={`px-4 py-2 text-[12px] uppercase tracking-wider transition-colors ${days === n ? "bg-[#a67c52] text-white" : "bg-white text-[#5a5348] hover:bg-[#f0e8dc]"}`}
            >
              Son {n} Gün
            </button>
          ))}
        </div>
      </div>

      {loading && <p className="text-[#8b8271]">Yükleniyor…</p>}

      {!loading && data && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-5 gap-3 mb-8">
            <Metric icon={Users} label="Ziyaretçi" value={data.unique_visitors} accent="#a67c52" />
            <Metric icon={Eye} label="Görüntüleme" value={data.total_pageviews} accent="#6b8e9e" />
            <Metric icon={MessageCircle} label="WhatsApp" value={data.whatsapp_clicks} accent="#25D366" />
            <Metric icon={FileDown} label="Katalog" value={data.catalog_downloads} accent="#c08552" />
            <Metric icon={Mail} testid="Mesaj" label={`Mesaj (toplam ${data.messages_total})`} value={data.messages_period} accent="#9a6a9a" />
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Daily chart */}
            <div className="lg:col-span-2 bg-white border border-[#e6ddd0] p-5">
              <h3 className="text-[13px] uppercase tracking-wider text-[#8b8271] mb-4 flex items-center gap-2"><TrendingUp size={15} /> Günlük Sayfa Görüntüleme</h3>
              <div className="flex items-end gap-[3px] h-40">
                {data.daily.map((d) => (
                  <div key={d.date} className="flex-1 flex flex-col items-center justify-end group relative" title={`${d.date}: ${d.count}`}>
                    <div
                      className="w-full bg-[#a67c52] hover:bg-[#8f6844] transition-colors rounded-t"
                      style={{ height: `${(d.count / maxDaily) * 100}%`, minHeight: d.count > 0 ? "3px" : "0" }}
                    />
                  </div>
                ))}
              </div>
              <div className="flex justify-between text-[10px] text-[#a89c88] mt-2">
                <span>{data.daily[0]?.date?.slice(5)}</span>
                <span>{data.daily[data.daily.length - 1]?.date?.slice(5)}</span>
              </div>
            </div>

            {/* Sources */}
            <div className="bg-white border border-[#e6ddd0] p-5">
              <h3 className="text-[13px] uppercase tracking-wider text-[#8b8271] mb-4 flex items-center gap-2"><Globe size={15} /> Trafik Kaynakları</h3>
              {data.sources.length === 0 && <p className="text-[13px] text-[#a89c88]">Henüz veri yok.</p>}
              <div className="space-y-3">
                {data.sources.map((s) => (
                  <div key={s.source} data-testid={`source-${s.source}`}>
                    <div className="flex justify-between text-[13px] text-[#5a5348] mb-1">
                      <span>{SOURCE_LABEL[s.source] || s.source}</span>
                      <span className="font-medium">{s.count}</span>
                    </div>
                    <div className="h-2 bg-[#efe7db] rounded">
                      <div className="h-2 bg-[#a67c52] rounded" style={{ width: `${(s.count / totalSessions) * 100}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Top pages */}
          <div className="bg-white border border-[#e6ddd0] p-5 mt-6">
            <h3 className="text-[13px] uppercase tracking-wider text-[#8b8271] mb-4">En Çok Girilen Sayfalar</h3>
            {data.top_pages.length === 0 && <p className="text-[13px] text-[#a89c88]">Henüz veri yok.</p>}
            <div className="space-y-2">
              {data.top_pages.map((p, i) => (
                <div key={p.path} className="flex items-center justify-between text-[13px] border-b border-[#f0e8dc] pb-2 last:border-0">
                  <span className="text-[#211d18]"><span className="text-[#a89c88] mr-2">{i + 1}.</span>{pathLabel(p.path)}</span>
                  <span className="text-[#8b8271]">{p.count}</span>
                </div>
              ))}
            </div>
          </div>

          <p className="text-[11px] text-[#a89c88] mt-6">Not: Ziyaretçi ve tıklama verileri takip kodunun eklendiği andan itibaren toplanır. Google arama kelimeleri için Google Search Console entegrasyonu ayrıca eklenebilir.</p>
        </>
      )}
    </div>
  );
};

export default AdminDashboard;
